/**
 * Pagination Utilities
 * Reusable pagination component and styles for consistent pagination across the application
 */

import React from "react";
import PropTypes from "prop-types";
import { Pagination } from "@mui/material";
import PaginationItem from "@mui/material/PaginationItem";
// import poweredByImage from "../assets/new icons/Powered By Comply Exchange.png";

/**
 * Default pagination styles
 */
export const paginationStyles = {
  "& .MuiPaginationItem-root": {
    background: "white",
    color: "#000",
    borderRadius: "6px",
    border: "1px solid #e0e0e0",
    fontSize: "13px",
    fontWeight: 500,
    minWidth: "32px",
    height: "32px",
    "&:hover": {
      backgroundColor: "#f0f0f0",
      color: "#000",
      borderColor: "#bbb",
    },
  },
  "& .MuiPaginationItem-page.Mui-selected": {
    backgroundColor: "var(--btn-primary-bg, #000)",
    color: "white",
    border: "1px solid var(--btn-primary-bg, #000)",
    "&:hover": {
      backgroundColor: "var(--application-secondary-color, #333)",
      color: "white",
    },
  },
  "& .MuiPaginationItem-previousNext": {
    color: "#000",
    fontWeight: 500,
    fontSize: "13px",

    minWidth: "52px",
  },
  "& .MuiPagination-ul": {
    gap: "2px",
  },
  "& .MuiPaginationItem-ellipsis": {
    margin: 0,
    border: "none",
    background: "transparent",
  },
};

/**
 * Default pagination style object for inline styles
 */
export const paginationStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  minHeight: "52px",
  boxSizing: "border-box",
};

/**
 * Powered-by footer row — same height/layout as ConditionalPagination row.
 * Use on pages that have no pagination but still need the branding footer.
 */
export const PoweredByRow = () => (
  <div style={paginationStyle}>
    {/* <img
      src={poweredByImage}
      alt="Powered by Comply Exchange"
      style={{ height: 40, objectFit: "contain", pointerEvents: "none", marginLeft: "30px" }}
    /> */}
  </div>
);

/**
 * Reusable Pagination Component
 * @param {Object} props - Component props
 * @param {number} props.count - Total number of pages
 * @param {number} props.page - Current page (1-based)
 * @param {Function} props.onChange - Page change handler
 * @param {Object} props.style - Custom inline styles
 * @param {Object} props.sx - Custom MUI sx styles
 * @param {boolean} props.hidePrevButton - Hide previous button
 * @param {boolean} props.hideNextButton - Hide next button
 * @param {boolean} props.showFirstButton - Show first page button
 * @param {boolean} props.showLastButton - Show last page button
 * @param {number} props.size - Size of pagination ('small', 'medium', 'large')
 * @param {string} props.color - Color theme ('primary', 'secondary', 'standard')
 * @param {boolean} props.disabled - Disable pagination
 * @param {number} props.siblingCount - Number of siblings to show
 * @param {number} props.boundaryCount - Number of boundary items to show
 */
const PrevLabel = () => <span>Prev</span>;
const NextLabel = () => <span>Next</span>;

/**
 * Conditional Pagination Component
 * Only shows pagination when there are more items than the specified threshold
 * @param {Object} props - Component props
 * @param {number} props.itemCount - Total number of items
 * @param {number} props.itemsPerPage - Items per page
 * @param {number} props.threshold - Minimum items to show pagination (default: 10)
 * @param {Object} props.paginationProps - Props to pass to ReusablePagination
 */
export const ConditionalPagination = ({
  itemCount,
  itemsPerPage = 10,
  threshold = 10,
  paginationProps = {},
  ...otherProps
}) => {
  const totalPages = Math.ceil(itemCount / itemsPerPage);
  const showPagination = itemCount > threshold;

  return (
    <div style={paginationStyle}>
      {/* <img
        src={poweredByImage}
        alt="Powered by Comply Exchange"
        style={{ height: 40, objectFit: "contain", pointerEvents: "none", marginLeft: "30px" }}
      /> */}
      {showPagination && (
        <Pagination
          count={totalPages}
          sx={paginationStyles}
          renderItem={(item) => (
            <PaginationItem slots={{ previous: PrevLabel, next: NextLabel }} {...item} />
          )}
          {...paginationProps}
          {...otherProps}
        />
      )}
    </div>
  );
};

ConditionalPagination.propTypes = {
  itemCount: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number,
  threshold: PropTypes.number,
  paginationProps: PropTypes.object,
};

/**
 * Pagination hook for managing pagination state
 * @param {number} initialPage - Initial page number
 * @param {number} itemsPerPage - Items per page
 * @returns {Object} Pagination state and handlers
 */
export const usePagination = (initialPage = 1, itemsPerPage = 10) => {
  const [currentPage, setCurrentPage] = React.useState(initialPage);
  const [pageSize, setPageSize] = React.useState(itemsPerPage);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const resetPagination = () => {
    setCurrentPage(1);
  };

  return {
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    resetPagination,
    setCurrentPage,
    setPageSize,
  };
};

/**
 * Hook that calculates how many rows fit in a container based on its measured height.
 * Uses a callback ref so it fires correctly even when the container is conditionally rendered.
 * Attach the returned ref to the scrollable container div.
 * @param {number} rowHeight - Height of a single row in px (default: 44)
 * @returns {{ ref: function, rowsPerPage: number }}
 */
export const useDynamicRowsPerPage = (rowHeight = 44) => {
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const observerRef = React.useRef(null);

  const ref = React.useCallback(
    (node) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!node) return;

      const calculate = () => {
        const height = node.getBoundingClientRect().height;
        const count = Math.max(1, Math.floor(height / rowHeight));
        setRowsPerPage(count);
      };

      calculate();
      observerRef.current = new ResizeObserver(calculate);
      observerRef.current.observe(node);
    },
    [rowHeight]
  );

  return { ref, rowsPerPage };
};

/**
 * Pagination constants
 */
export const PAGINATION_CONSTANTS = {
  DEFAULT_ITEMS_PER_PAGE: 10,
  DEFAULT_THRESHOLD: 10,
  DEFAULT_SIBLING_COUNT: 1,
  DEFAULT_BOUNDARY_COUNT: 1,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100],
};

const paginationUtils = {
  ConditionalPagination,
  PoweredByRow,
  useDynamicRowsPerPage,
  paginationStyles,
  paginationStyle,
  PAGINATION_CONSTANTS,
};

export default paginationUtils;
